// src/auth/profile.js
//
// Phase 2 Sprint 2: Firestore users/{uid} profile data access.
//
// Responsibilities:
//   - ensureUserProfile(user): idempotent create-if-missing using getDoc -> setDoc two-call pattern.
//     Preserves immutable createdAt: existing docs are NOT overwritten.
//   - fetchUserProfile(uid):   read-only fetch returning profile data.
//
// Import-boundary contract (P6 anti-goals):
//   This module is DATA-ACCESS ONLY. Imports allowed: db, AUTH_MODE, Firestore primitives.
//   DO NOT import: AuthGuard, AdminGuard, LoginPage, Navbar, useAppState, Stripe,
//   Cloud Functions, Firebase Admin SDK. The bridge into useAppState lives in AuthProvider.
//
// Mock-mode contract (P7 invariant):
//   These functions throw if called when AUTH_MODE === 'mock'. Callers must branch on
//   AUTH_MODE before calling. Mock mode does NO Firestore work whatsoever.

import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, AUTH_MODE } from './firebase.js';

// Display-only fallback when user has no displayName and no email prefix.
// Not authoritative identity. Not localized in Sprint 2.
export const DEFAULT_DISPLAY_NAME = 'Student';

function assertNotMockMode(callerName) {
    if (AUTH_MODE === 'mock') {
        throw new Error(
            `profile.js: ${callerName} must not be called in mock mode. ` +
            'Callers must branch on AUTH_MODE before invoking.'
        );
    }
    if (!db) {
        throw new Error(
            `profile.js: ${callerName} called but Firestore db is null. ` +
            'Check firebase.js initialization.'
        );
    }
}

// Idempotent: creates users/{uid} only if missing. Existing docs are NOT overwritten,
// preserving immutable createdAt. Concurrent-tab race tolerated: both writes produce
// identical content (modulo serverTimestamp value); last-write-wins acceptable.
//
// displayName fallback (P5): user.displayName || email-prefix || DEFAULT_DISPLAY_NAME.
// role: 'student' is pinned by Firestore rules (v1.1-beta); passive metadata only.
export async function ensureUserProfile(user) {
    assertNotMockMode('ensureUserProfile');
    if (!user?.uid) {
        throw new Error('ensureUserProfile: user.uid required');
    }
    const ref = doc(db, 'users', user.uid);
    const snap = await getDoc(ref);
    if (snap.exists()) return; // no-op; preserve immutable createdAt
    const displayName =
        user.displayName ||
        user.email?.split('@')[0] ||
        DEFAULT_DISPLAY_NAME;
    await setDoc(ref, {
        uid: user.uid,
        email: user.email,
        displayName,
        role: 'student',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    });
}

// Read-only fetch. Returns profile data object, or null if doc missing.
// Caller responsible for handling null (e.g., AuthProvider treats as a sync failure).
export async function fetchUserProfile(uid) {
    assertNotMockMode('fetchUserProfile');
    if (!uid) {
        throw new Error('fetchUserProfile: uid required');
    }
    const ref = doc(db, 'users', uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return snap.data();
}